import type { FunctionArgs, FunctionReturnType } from "convex/server";
import type { Id } from "@convex/_generated/dataModel";
import { api } from "@convex/_generated/api";
import { readUnknownProperty } from "@shared/validation/narrow";

export type UploadOptions = Omit<
  FunctionArgs<typeof api.files.mutations.saveFileMetadata>,
  "filename" | "mimeType" | "size" | "storageId"
>;

export type UploadResult = FunctionReturnType<
  typeof api.files.mutations.saveFileMetadata
>;

export interface UploadDependencies {
  generateUploadUrl: (args: Record<string, never>) => Promise<string>;
  saveFileMetadata: (
    args: FunctionArgs<typeof api.files.mutations.saveFileMetadata>,
  ) => Promise<UploadResult>;
  fetchUpload?: typeof fetch;
}

export type FileUploadOutcome =
  | { status: "fulfilled"; file: File; result: UploadResult }
  | { status: "rejected"; file: File; error: Error };

function toError(error: unknown): Error {
  return error instanceof Error ? error : new Error(String(error));
}

function isUploadedStorageId(value: unknown): value is Id<"_storage"> {
  return typeof value === "string";
}

export async function readStorageId(
  response: Response,
): Promise<Id<"_storage">> {
  const payload: unknown = await response.json();
  const storageId = readUnknownProperty(payload, "storageId");
  if (!isUploadedStorageId(storageId)) {
    throw new Error("Upload completed without a storage ID.");
  }
  return storageId;
}

export async function uploadFileToConvex(
  file: File,
  options: UploadOptions,
  dependencies: UploadDependencies,
  onProgress?: (progress: number) => void,
): Promise<UploadResult> {
  onProgress?.(10);
  const uploadUrl = await dependencies.generateUploadUrl({});
  onProgress?.(20);
  const response = await (dependencies.fetchUpload ?? fetch)(uploadUrl, {
    method: "POST",
    headers: { "Content-Type": file.type },
    body: file,
  });
  if (!response.ok) {
    throw new Error(`Upload failed: ${response.statusText}`);
  }
  const storageId = await readStorageId(response);
  onProgress?.(70);
  const result = await dependencies.saveFileMetadata({
    ...options,
    storageId,
    filename: file.name,
    mimeType: file.type,
    size: file.size,
  });
  onProgress?.(100);
  return result;
}

export async function uploadFilesToConvex(
  files: File[],
  options: UploadOptions,
  dependencies: UploadDependencies,
  onProgress?: (index: number, progress: number) => void,
): Promise<FileUploadOutcome[]> {
  return await Promise.all(
    files.map(async (file, index): Promise<FileUploadOutcome> => {
      try {
        const result = await uploadFileToConvex(
          file,
          options,
          dependencies,
          (progress) => onProgress?.(index, progress),
        );
        return { status: "fulfilled", file, result };
      } catch (error) {
        return { status: "rejected", file, error: toError(error) };
      }
    }),
  );
}
